import React, { useEffect, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Stack, Typography,
} from '@mui/material';
import { Check, ContentCopy, EmailOutlined, IosShare, SmsOutlined } from '@mui/icons-material';
import { tagInviteService, TagInviteLink } from '../services/tagInviteService';
import { buildTagClaimLink } from '../utils/invite';
import { useNotification } from './NotificationContext';

interface TagInviteDialogProps {
  postId: string;
  onClose: () => void;
}

/**
 * After tagging someone who isn't on the app yet: a personal claim link per person. Messages are
 * drafted on the author's own device (share sheet / SMS / mail client) — the app never contacts
 * them itself, so tagging can't be used to spam anyone, and no phone number or email is stored.
 */
const TagInviteDialog: React.FC<TagInviteDialogProps> = ({ postId, onClose }) => {
  const { showSuccess, showError } = useNotification();
  const [links, setLinks] = useState<TagInviteLink[] | null>(null);
  const [failed, setFailed] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    let cancelled = false;
    tagInviteService.getMine(postId)
      .then(l => { if (!cancelled) setLinks(l); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [postId]);

  const linkFor = (l: TagInviteLink) => buildTagClaimLink(postId, l.tagId, l.code);
  const messageFor = (l: TagInviteLink) =>
    `I tagged you in a post on New Achievement Scuba — where divers connect! See it here: ${linkFor(l)}`;

  const copy = async (l: TagInviteLink) => {
    try {
      await navigator.clipboard.writeText(linkFor(l));
      showSuccess(`Link for ${l.name} copied`);
    } catch {
      showError('Could not copy the link');
    }
  };

  const nativeShare = async (l: TagInviteLink) => {
    try {
      await navigator.share({ title: 'You were tagged in a dive post', text: 'I tagged you in a post — where divers connect!', url: linkFor(l) });
    } catch { /* dismissed the share sheet */ }
  };

  const pending = (links ?? []).filter(l => !l.claimed);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Let them know you tagged them</DialogTitle>
      <DialogContent>
        {!links && !failed && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>}
        {failed && <DialogContentText>Couldn't load your invite links. You can reopen this from the post.</DialogContentText>}
        {links && pending.length === 0 && (
          <DialogContentText>Everyone you tagged has already joined.</DialogContentText>
        )}
        {pending.length > 0 && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              They aren't on the app yet. Send each person their link — when they join from it, they'll see this post and start out following you.
            </DialogContentText>
            <Stack spacing={2} divider={<Divider flexItem />}>
              {pending.map(l => (
                <Stack key={l.tagId} spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{l.name}</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {canNativeShare && <Button size="small" variant="contained" startIcon={<IosShare />} onClick={() => nativeShare(l)}>Share…</Button>}
                    <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={() => copy(l)}>Copy link</Button>
                    <Button size="small" variant="outlined" startIcon={<SmsOutlined />} component="a" href={`sms:?&body=${encodeURIComponent(messageFor(l))}`}>Text</Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailOutlined />}
                      component="a"
                      href={`mailto:?subject=${encodeURIComponent('You were tagged in a dive post')}&body=${encodeURIComponent(messageFor(l))}`}
                    >
                      Email
                    </Button>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {links && links.some(l => l.claimed) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
            <Check fontSize="inherit" /> {links.filter(l => l.claimed).map(l => l.name).join(', ')} already joined
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagInviteDialog;
